<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import mapUtility from "@/js/mapUtility"
import nascarData from "@/js/nascarData";

const store = useStore();
const isMounted = ref(false);
const nonTop5Drivers = ref([]);
const mapClass = ref("");
const mapScale = ref(1.0);
const mapOffsetX = ref(0);
const mapOffsetY = ref(0);

onMounted(async () => {
  isMounted.value = true;
  mapClass.value = mapUtility.mapCornersCurrent.class;

  //Subsribe to all non top 5 drivers
  const results = store.getters.results;
  if (results.length > 5) {
    for (let i = 5; i < results.length; i++) {
      const index = i - 5;
      let driverData = results[i];

      let { vehicleNo, driverID } = nascarData.getVehicleNoFromData(driverData);

      let boundFunction = updateLocation.bind(this, index)
      const channel = nascarData.realtimeClient.channels.get(`vehicle${vehicleNo}`);
      channel.subscribe(boundFunction);

      nonTop5Drivers.value.push({
        boundFunction: boundFunction,
        vehicleNo: vehicleNo,
        telemetry: null
      })
    }
  }
});

onUnmounted(() => {
  isMounted.value = false;

  //Unsubsribe to all non top 5 drivers
  for (let i = 0; i < nonTop5Drivers.value.length; i++) {
    const channel = nascarData.realtimeClient.channels.get(`vehicle${nonTop5Drivers.value[i].vehicleNo}`);
    channel.unsubscribe(nonTop5Drivers.value[i].boundFunction);
  }
})


function updateLocation(index, message) {
  try {
    let telemetry = message.data;
    if (typeof message.data == 'string') {
      telemetry = JSON.parse(message.data);
    }

    console.log("map telemetry received: ")
    console.log(telemetry)

    let mapPositions = mapUtility.gpsToPixel(telemetry.location[0], telemetry.location[1],
      mapUtility.mapCornersCurrent, mapUtility.mapCornersCurrent.width, mapUtility.mapCornersCurrent.height);
    if (mapPositions == null) {
      telemetry.remappedLocations = {
        x: 0,
        y: 0
      }
    } else {
      telemetry.remappedLocations = {
        x: mapPositions.x,
        y: mapPositions.y
      }
    }

    nonTop5Drivers.value[index].telemetry = telemetry;
  } catch (e) {
    console.error(`[vehicle position ${index}] Failed to parse message:`, e);
  }
}

function changeMapMode(index) {
  store.commit("mapMode", index);
}

function getMapPosTranslate(location, dotWidth) {
  return `translate(${location.x - (dotWidth/2)}px, ${location.y - (dotWidth / 2) }px)`;
}


</script>

<template>
  <div class="map-2d flex-descending-column">
    <div class="map-area" :class="`${mapClass}`">
      <div class="map-area" :class="`${mapClass}`"
        :style="{ transform: `scale(${mapScale}) translate(${mapOffsetX}%, ${mapOffsetY}%)` }">
        <template v-if="store.getters.mapMode == 0">
          <template v-for="(telemetry, index) in store.getters.topFiveTelemetry">
            <div class="driver-point" v-if="telemetry != null && telemetry.remappedLocations != null" :style="{
              'transform': getMapPosTranslate(telemetry.remappedLocations, 35)
            }">
              <div class="connecting-line-center" :class="`connecting-line-${index}`">
                <div class="connecting-line" :style="{ backgroundColor: store.getters.allDriverColors[index] }"></div>
              </div>
              <div class="driver-dot-big" :style="{ backgroundColor: store.getters.allDriverColors[index] }">
              </div>
              <div class="number-circle flex-center" :class="`number-pos-${index}`"
                :style="{ borderColor: store.getters.allDriverColors[index] }">
                <img :src="`https://cf.nascar.com/data/images/carbadges/1/${telemetry.vehicle_id}.png`">
              </div>
            </div>
          </template>
          <template v-for="(driver, index) in nonTop5Drivers">
            <div class="driver-point" v-if="driver.telemetry != null && driver.telemetry.remappedLocations != null"
              :style="{
                'transform': getMapPosTranslate(driver.telemetry.remappedLocations, 35)
              }">
              <div class="driver-dot" :style="{ backgroundColor: store.getters.allDriverColors[index] }"></div>
            </div>
          </template>
        </template>
        <template v-if="store.getters.mapMode == 1">
          <div class="driver-point"
            v-if="store.getters.favoriteDriver.telemetry != null && store.getters.favoriteDriver.telemetry.remappedLocations != null"
            :style="{
              'transform': getMapPosTranslate(store.getters.favoriteDriver.telemetry.remappedLocations, 35)
            }">
            <div class="connecting-line-center" :class="`connecting-line-0`">
              <div class="connecting-line" :style="{ backgroundColor: store.getters.allDriverColors[0] }"></div>
            </div>
            <div class="driver-dot-big" :style="{ backgroundColor: store.getters.allDriverColors[0] }">
            </div>
            <div class="number-circle flex-center" :class="`number-pos-0`"
              :style="{ borderColor: store.getters.allDriverColors[0] }">
              <img :src="`https://cf.nascar.com/data/images/carbadges/1/${store.getters.favoriteDriver.telemetry.vehicle_id}.png`">
            </div>
          </div>
        </template>
        
      </div>
    </div>
    <div class="button-area">
      <button class="top5-button focusable" @click="changeMapMode(0)">
        <img src="../../public/assets/imgs/top5.png">
        <div class="text">
          Top 5
        </div>
      </button>
      <button class="favorite-button flex-center focusable" @click="changeMapMode(1)"
        v-if="store.getters.favoriteDriver.telemetry != null">
        <img src="../../public/assets/imgs/FavDriver_Icon.svg">
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../sass/GlobalClasses';
@import '../sass/Settings';

.driver-point {
  position: absolute;
  $full-width: 35;
  $full-height: 35;

  $dot-width: 6;
  $dot-height: 6;

  $dot-big-width: 12;
  $dot-big-height: 12;

  width: vw($full-width);
  height: vw($full-height);

  transform: translate(-50%, -50%);
  transition: all 0.2s linear;

  .connecting-line-center {
    position: absolute;
    width: vw(1);
    height: vw(1);
    top: vw($full-width/2);
    left: vw($full-height/2);

    .connecting-line {
      position: absolute;
      width: vw(2);
      height: vw(30);
      top: vw(-30);
      left: vw(-1);
      background-color: white;
    }
  }

  .connecting-line-0 {
    transform: rotate(-30deg)
  }

  .connecting-line-1 {
    transform: rotate(-135deg)
  }

  .connecting-line-2 {
    transform: rotate(0deg)
  }

  .connecting-line-3 {
    transform: rotate(155deg)
  }

  .connecting-line-4 {
    transform: rotate(45deg)
  }


  .number-circle {
    $number-circle-radius: 15;
    position: absolute;
    width: vw($number-circle-radius * 2);
    height: vw($number-circle-radius * 2);
    border-radius: vw($number-circle-radius);
    border: solid 3px white;
    overflow: hidden;
    background-color: #C7C7C7;
  }

  .number-pos-0 {
    right: vw(7);
    top: vw(-25);
  }

  .number-pos-1 {
    right: vw(20);
    top: vw(20);
  }

  .number-pos-2 {
    right: vw(0);
    top: vw(-30);
  }

  .number-pos-3{
    right: vw(-7);
    top: vw(25);
  }

  .number-pos-4{
    right: vw(-20);
    top: vw(-20);
  }

  img {
    position: relative;
    width: 100%;
  }

  .driver-dot {
    position: absolute;
    left: vw(($full-width - $dot-width) / 2);
    top: vw(($full-height - $dot-height) / 2);
    width: vw($dot-width);
    height: vw($dot-height);
    border-radius: vw($dot-width/2);
    background-color: red;
  }

  .driver-dot-big {
    position: absolute;
    left: vw(($full-width - $dot-big-width) / 2);
    top: vw(($full-height - $dot-big-height) / 2);
    width: vw($dot-big-width);
    height: vw($dot-big-height);
    border-radius: vw($dot-big-width/2);
    background-color: red;
  }
}

.map-2d {
  padding: vw(20);
  position: fixed;
  left: vw(40);
  top: vw(40);
  right: vw(40);
  bottom: vw(40);

  .map-area {
    position: relative;
    background-size: 100% 100%;
    border-radius: vw(15);
  }

  .map-daytona {
    $width: 468;
    $height: 275;
    width: vw($width);
    height: vw($height);
    background-image: url("../../public/assets/imgs/track_daytona.png");
  }

  .map-nh {
    $width: 468;
    $height: 433;
    width: vw($width);
    height: vw($height);
    background-image: url("../../public/assets/imgs/new-hamp-map.png");
  }

  .map-sonoma {
    $width: 480;
    $height: 306;
    width: vw($width);
    height: vw($height);
    background-image: url("../../public/assets/imgs/track_daytona.png");
  }

  .button-area {
    position: relative;
    margin-top: vw(30);
    width: 100%;
    height: vw(44);

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    .top5-button {
      position: relative;
      width: vw(129);
      height: vw(44);
      border-radius: vw(22);

      background-color: #1C1C1E;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding-bottom: vw(2);

      img {
        position: relative;
        width: vw(31);
        height: vw(20);
      }

      .text {
        color: white;
        position: relative;
        font-size: vw(24);
        font-weight: 400;
        margin-left: vw(7);
      }
    }

    .favorite-button {
      position: relative;
      width: vw(44);
      height: vw(44);
      border-radius: vw(22);
      margin-left: vw(10);

      background-color: #1C1C1E;

      img {
        position: relative;
        width: vw(22);
        height: vw(22);
      }
    }
  }

}
</style>
